/* Real-time co-pilot guidance rendered in the dynamic canvas.
 *
 * Per the mockup (agentic_copilot_mockup_v4.html, all states): the
 * canvas shows ONE hero `.ai-card` at a time. As of advisor schema v2
 * (migration 0025), each card carries a type-discriminated `payload`
 * so the canvas renders mockup-aligned components per type:
 *   suggested_approach    — leads list + why-line  (mockup state 1)
 *   intent_root_cause     — k/v intent banner      (mockup state 2)
 *   competitor_battlecard — comparison table       (mockup state 3)
 *   offer_presentation    — bill-impact + features (mockup state 4)
 *   nudge                 — slim alert/sentiment banner
 *
 * Pre-0025 rows (call_advice.payload IS NULL) fall back to the generic
 * hero layout so historical calls keep rendering.
 *
 * Post-call: a horizontal timeline strip below the hero lets the agent
 * scrub through every card emitted during the call. Clicking a chip
 * swaps it into the hero slot; "Latest" returns to live tracking.
 */

import { useEffect, useMemo, useState } from "react";

import { API_URL, fmtTime } from "../lib/api";
import { OfferLadder } from "./OfferLadder";
import { type CallStatus } from "./useCurrentCall";

// ─── Types ─────────────────────────────────────────────────────────────
export type CardType =
  | "suggested_approach"
  | "intent_root_cause"
  | "competitor_battlecard"
  | "offer_presentation"
  | "troubleshooting"
  | "repair_action"
  | "nudge"
  // Wrap-up cards from advisor.on_call_end (added in 0026).
  | "call_outcome"
  | "coaching"
  // Legacy advisor output (pre-0025). Rendered with the generic hero
  // fallback; new advisor never produces these.
  | "opening"
  | "next_action"
  | "offer"
  | "sentiment"
  | "intent"
  | "alert";

export type SuggestedApproachPayload = {
  leads: string[];
  why: string;
};

type IntentRootCauseRow = {
  label: string;
  value: string;
  status: string;
};

export type IntentRootCausePayload = {
  rows: IntentRootCauseRow[];
  why: string;
};

type ComparisonRow = { feature: string; theirs: string; ours: string };

export type CompetitorBattlecardPayload = {
  competitor_name: string;
  comparison: ComparisonRow[];
  hooks: string[];
  why: string;
};

export type OfferPresentationPayload = {
  offer_code: string;
  current_monthly_cents: number;
  new_monthly_cents: number;
  lock_months: number;
  features: string[];
  leads: string[];
  why: string;
};

export type NudgePayload = {
  severity: "alert" | "sentiment";
  message: string;
  evidence: string;
};

type TroubleshootingStep = { step: string; expected: string };

export type TroubleshootingPayload = {
  symptom: string;
  likely_cause: string;
  steps: TroubleshootingStep[];
  escalate_if: string;
};

export type RepairActionPayload = {
  issue_type: string;
  severity: "low" | "medium" | "high" | "critical";
  ticket_action: string;
  eta: string;
  why: string;
};

type OutcomeRow = { label: string; value: string };

export type CallOutcomePayload = {
  outcome: "SAVED" | "LOST" | "DEFERRED" | "ESCALATED";
  summary: string;
  rows: OutcomeRow[];
  outcome_tag: string;
};

type CoachingScore = {
  label: string;
  value: number;
  tier: "good" | "mid" | "low";
};

type CoachingMoment = {
  timestamp_secs: number;
  text: string;
  quote: string;
};

type CoachingTrend = {
  save_rate_this_week: string;
  save_rate_last_week: string;
  competitor_saves_today: string;
  summary: string;
};

export type CoachingPayload = {
  overall_score: number;
  summary: string;
  scores: CoachingScore[];
  what_went_well: CoachingMoment;
  one_thing_to_try_next: CoachingMoment;
  trend: CoachingTrend;
};

export type Card = {
  id: number;
  ts: string;
  type: CardType;
  title: string;
  body: string;
  confidence: number | null;
  evidence: string | null;
  payload:
    | SuggestedApproachPayload
    | IntentRootCausePayload
    | CompetitorBattlecardPayload
    | OfferPresentationPayload
    | TroubleshootingPayload
    | RepairActionPayload
    | NudgePayload
    | CallOutcomePayload
    | CoachingPayload
    | null;
};

// Types eligible for the hero slot (the "one at a time" content). nudge
// + legacy alert/sentiment land in the slim banner row instead.
const HERO_TYPES: ReadonlySet<CardType> = new Set([
  "suggested_approach",
  "intent_root_cause",
  "competitor_battlecard",
  "offer_presentation",
  "call_outcome",
  "coaching",
  "next_action",
  "offer",
  "intent",
  "opening",
]);

const TYPE_TAG: Record<CardType, string> = {
  suggested_approach:    "SUGGESTED APPROACH",
  intent_root_cause:     "INTENT · ROOT CAUSE",
  competitor_battlecard: "COMPETITOR BATTLECARD",
  offer_presentation:    "RECOMMENDED OFFER",
  troubleshooting:       "TROUBLESHOOTING",
  repair_action:         "REPAIR ACTION",
  call_outcome:          "CALL OUTCOME",
  coaching:              "YOUR COACHING — THIS CALL",
  nudge:                 "NUDGE",
  opening:               "OPENING",
  next_action:           "NEXT BEST ACTION",
  offer:                 "OFFER",
  intent:                "INTENT",
  sentiment:             "SENTIMENT",
  alert:                 "ALERT",
};

function confidenceDots(c: number | null): { lit: number; label: string } {
  if (c === null) return { lit: 0, label: "—" };
  if (c < 0.5) return { lit: 1, label: "low" };
  if (c < 0.8) return { lit: 2, label: "mid" };
  return { lit: 3, label: "high" };
}

function fmtCents(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

// ─── Top-level component ───────────────────────────────────────────────
export function LiveAdvice({
  callId,
  callStatus,
}: {
  callId: number | undefined;
  callStatus?: CallStatus;
}) {
  const [cards, setCards] = useState<Card[]>([]);
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());
  const [pinnedId, setPinnedId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Reset everything on call change — old cards belong to a different call.
  useEffect(() => {
    setCards([]);
    setDismissed(new Set());
    setPinnedId(null);
    setErr(null);
  }, [callId]);

  useEffect(() => {
    if (callId === undefined) return;
    let cancelled = false;

    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/calls/${callId}/advice`);
        if (!r.ok) throw new Error(`advice ${r.status}`);
        const data = (await r.json()) as { advice: Card[] };
        if (!cancelled) setCards(data.advice ?? []);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      }
    })();

    const es = new EventSource(`${API_URL}/api/calls/${callId}/stream`);
    es.addEventListener("advice", (ev) => {
      try {
        const row = JSON.parse((ev as MessageEvent).data) as Card;
        setCards((cur) => (cur.some((x) => x.id === row.id) ? cur : [...cur, row]));
      } catch {
        /* skip malformed */
      }
    });
    return () => {
      cancelled = true;
      es.close();
    };
  }, [callId]);

  // Latest non-dismissed alert + sentiment populate the slim banner row.
  // Both legacy (type='alert' / 'sentiment') and new (type='nudge' with
  // payload.severity) shapes feed the same slot.
  const { alertCard, sentimentCard, latestHero } = useMemo(() => {
    let alert: Card | null = null;
    let sentiment: Card | null = null;
    let hero: Card | null = null;
    for (const c of cards) {
      if (dismissed.has(c.id)) continue;
      const sev = nudgeSeverity(c);
      if (sev === "alert") alert = c;
      else if (sev === "sentiment") sentiment = c;
      else if (HERO_TYPES.has(c.type)) hero = c;
    }
    return { alertCard: alert, sentimentCard: sentiment, latestHero: hero };
  }, [cards, dismissed]);

  // Hero-eligible cards (in order) drive the navigator. A pinned id
  // overrides "latest" so the agent can dwell on an earlier card.
  const heroTimeline = useMemo(
    () => cards.filter((c) => HERO_TYPES.has(c.type)),
    [cards],
  );
  const pinnedCard = useMemo(
    () => heroTimeline.find((c) => c.id === pinnedId) ?? null,
    [heroTimeline, pinnedId],
  );
  const hero = pinnedCard ?? latestHero;
  const heroIdx = useMemo(
    () => (hero ? heroTimeline.findIndex((c) => c.id === hero.id) : -1),
    [heroTimeline, hero],
  );

  const stepHero = (delta: number) => {
    if (heroIdx < 0) return;
    const next = heroIdx + delta;
    if (next < 0 || next >= heroTimeline.length) return;
    const target = heroTimeline[next];
    if (!target) return;
    // The last card is "latest" — clear the pin so live tracking
    // resumes on the next inbound card. Otherwise pin to the target.
    if (next === heroTimeline.length - 1) setPinnedId(null);
    else setPinnedId(target.id);
  };

  const dismiss = (id: number) => {
    setDismissed((cur) => {
      const next = new Set(cur);
      next.add(id);
      return next;
    });
    // Don't strand the navigator on a dismissed card.
    if (pinnedId === id) setPinnedId(null);
  };

  if (callId === undefined) return null;

  const nothingYet =
    !err && hero === null && alertCard === null && sentimentCard === null;
  // The navigator is most useful in wrap-up but also helps mid-call
  // when the agent wants to revisit earlier guidance. Show whenever
  // there are 2+ hero cards to flip through.
  const showNavigator = heroTimeline.length >= 2;

  return (
    <div id="agent-desk-live-advice" className="agent-desk-live-advice">
      {err && <div className="dial-err">{err}</div>}

      {alertCard && (
        <NudgeBanner
          card={alertCard}
          severity="alert"
          onDismiss={() => dismiss(alertCard.id)}
        />
      )}
      {sentimentCard && (
        <NudgeBanner
          card={sentimentCard}
          severity="sentiment"
          onDismiss={() => dismiss(sentimentCard.id)}
        />
      )}

      {hero ? (
        <>
          <HeroSlot
            card={hero}
            callId={callId}
            onDismiss={() => dismiss(hero.id)}
          />
          {heroTimeline.length > 1 && (
            <HeroPagination
              currentIdx={heroIdx}
              total={heroTimeline.length}
              onPrev={() => stepHero(-1)}
              onNext={() => stepHero(1)}
              onLatest={() => setPinnedId(null)}
              isPinned={pinnedCard !== null}
            />
          )}
        </>
      ) : nothingYet ? (
        <EmptyState />
      ) : null}

      {showNavigator && (
        <Navigator
          cards={heroTimeline}
          activeId={hero?.id ?? null}
          callStatus={callStatus}
          onPick={(id) => {
            // Picking the latest clears the pin so live tracking resumes.
            const isLast = heroTimeline[heroTimeline.length - 1]?.id === id;
            setPinnedId(isLast ? null : id);
          }}
        />
      )}
    </div>
  );
}

function nudgeSeverity(card: Card): "alert" | "sentiment" | null {
  if (card.type === "nudge") {
    const p = card.payload as NudgePayload | null;
    return p?.severity ?? "sentiment";
  }
  if (card.type === "alert") return "alert";
  if (card.type === "sentiment") return "sentiment";
  return null;
}

// ─── Hero slot (type switch) ───────────────────────────────────────────
function HeroSlot({
  card,
  callId,
  onDismiss,
}: {
  card: Card;
  callId: number | undefined;
  onDismiss: () => void;
}) {
  // Pre-0025 rows (no payload) and unknown variants land on the generic
  // hero so historical calls don't blow up on the new component switch.
  if (!card.payload) {
    return <GenericHero card={card} onDismiss={onDismiss} />;
  }
  switch (card.type) {
    case "suggested_approach":
      return <SuggestedApproachCard card={card} onDismiss={onDismiss} />;
    case "intent_root_cause":
      return <IntentRootCauseCard card={card} onDismiss={onDismiss} />;
    case "competitor_battlecard":
      return <CompetitorBattlecardCard card={card} onDismiss={onDismiss} />;
    case "offer_presentation":
      return (
        <OfferPresentationCard
          card={card}
          callId={callId}
          onDismiss={onDismiss}
        />
      );
    case "troubleshooting":
      return <TroubleshootingCard card={card} onDismiss={onDismiss} />;
    case "repair_action":
      return <RepairActionCard card={card} onDismiss={onDismiss} />;
    case "call_outcome":
      return <CallOutcomeCard card={card} onDismiss={onDismiss} />;
    case "coaching":
      return <CoachingCard card={card} onDismiss={onDismiss} />;
    default:
      return <GenericHero card={card} onDismiss={onDismiss} />;
  }
}

// ─── Shared header used by every hero variant ──────────────────────────
function HeroHead({
  card,
  onDismiss,
}: {
  card: Card;
  onDismiss: () => void;
}) {
  const conf = confidenceDots(card.confidence);
  return (
    <header className="agent-desk-ai-card-head">
      <span className="agent-desk-ai-tag">
        <span className="agent-desk-ai-sparkle" aria-hidden>✦</span>
        {TYPE_TAG[card.type] ?? card.type.toUpperCase()}
      </span>
      <span
        className="agent-desk-ai-confidence"
        title={`Advisor self-reported confidence: ${conf.label}`}
      >
        <span className={`dot ${conf.lit >= 1 ? "on" : ""}`} />
        <span className={`dot ${conf.lit >= 2 ? "on" : ""}`} />
        <span className={`dot ${conf.lit >= 3 ? "on" : ""}`} />
        {conf.label}
      </span>
      <button
        type="button"
        className="agent-desk-ai-dismiss"
        onClick={onDismiss}
        aria-label="Dismiss"
        title="Dismiss"
      >
        ×
      </button>
    </header>
  );
}

function WhyLine({
  why,
  label = "Why this surfaces",
}: {
  why: string;
  label?: string;
}) {
  if (!why) return null;
  // Density rule: a "why" only earns a row when it's a real fragment
  // (≤8 words). Anything longer is the LLM regressing into prose, and
  // the payload-specific rows / comparison tables do the heavy lifting
  // — keep the canvas scannable rather than show a paragraph.
  const wordCount = why.trim().split(/\s+/).length;
  if (wordCount > 8) return null;
  return (
    <div className="agent-desk-ai-why">
      <span className="agent-desk-ai-why-label">{label}:</span>{" "}
      <span className="agent-desk-ai-why-text">{why}</span>
    </div>
  );
}

// ─── Generic fallback (legacy rows + unknown types) ────────────────────
function GenericHero({
  card,
  onDismiss,
}: {
  card: Card;
  onDismiss: () => void;
}) {
  return (
    <article className={`agent-desk-ai-card is-${card.type}`}>
      <HeroHead card={card} onDismiss={onDismiss} />
      <h3 className="agent-desk-ai-title">{card.title}</h3>
      {card.evidence && (
        <div className="agent-desk-ai-evidence">
          <span className="agent-desk-ai-evidence-label">Heard at</span>
          <span className="agent-desk-ai-evidence-time">{fmtTime(card.ts)}</span>
          <div className="agent-desk-ai-evidence-quote">"{card.evidence}"</div>
        </div>
      )}
    </article>
  );
}

// ─── Variant: suggested_approach (mockup state 1) ──────────────────────
export function SuggestedApproachCard({
  card,
  onDismiss,
}: {
  card: Card;
  onDismiss: () => void;
}) {
  const p = card.payload as SuggestedApproachPayload;
  return (
    <article className="agent-desk-ai-card is-suggested_approach">
      <HeroHead card={card} onDismiss={onDismiss} />
      <h3 className="agent-desk-ai-title">{card.title}</h3>
      {p.leads?.length > 0 && <PointList items={p.leads} />}
      <WhyLine why={p.why} label="Why this surfaces" />
    </article>
  );
}

// PointList — bordered horizontal boxes (mockup `.point`). Each item
// gets a small dot bullet on the left. When an item is "Label: text",
// the label gets bolded inline. Used by suggested_approach leads,
// competitor hooks, and offer_presentation leads.
function PointList({ items }: { items: string[] }) {
  return (
    <div className="agent-desk-ai-points">
      {items.map((item, i) => {
        const split = splitLabeledPoint(item);
        return (
          <div key={i} className="agent-desk-ai-point">
            <span className="agent-desk-ai-point-dot" aria-hidden />
            <span className="agent-desk-ai-point-text">
              {split ? (
                <>
                  <strong>{split.label}:</strong> {split.text}
                </>
              ) : (
                item
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// "Hook: $120 is intro only…" → { label: "Hook", text: "$120 is …" }
// Returns null when the string isn't a labeled point so plain text
// renders unchanged.
function splitLabeledPoint(
  raw: string,
): { label: string; text: string } | null {
  const m = raw.match(/^([A-Za-z][A-Za-z ]{0,18}):\s+(.+)$/);
  if (!m) return null;
  const [, label, text] = m;
  if (label === undefined || text === undefined) return null;
  return { label, text };
}

// ─── Variant: intent_root_cause (mockup state 2) ───────────────────────
// Each row is its own bordered box (mockup `.hero-row`) with an
// uppercase label on the left, value on the right, optional severity
// chip. The advisor produces 2-4 rows: PRIMARY INTENT and ROOT CAUSE
// always; BILL IMPACT and SENTIMENT when supported by the transcript.
export function IntentRootCauseCard({
  card,
  onDismiss,
}: {
  card: Card;
  onDismiss: () => void;
}) {
  const p = card.payload as IntentRootCausePayload;
  return (
    <article className="agent-desk-ai-card is-intent_root_cause">
      <HeroHead card={card} onDismiss={onDismiss} />
      <h3 className="agent-desk-ai-title">{card.title}</h3>
      {p.rows?.length > 0 && (
        <div className="agent-desk-ai-rows">
          {p.rows.map((row, i) => (
            <div key={i} className="agent-desk-ai-row">
              <span className="agent-desk-ai-row-k">{row.label}</span>
              <span className="agent-desk-ai-row-v">{row.value}</span>
              {row.status && !isRedundantStatus(row.status, card.confidence) && (
                <span
                  className={`agent-desk-ai-row-status is-${chipSeverity(row.status)}`}
                >
                  {row.status}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      <WhyLine why={p.why} label="Why this surfaces" />
    </article>
  );
}

// Drop chips whose only signal is "we're confident in this" — the
// confidence dots in HeroHead already say that. Density rule: don't
// repeat info another field already shows. When confidence is high
// (≥0.85, which is what dots render as the third-filled tier),
// "CONFIRMED" / "VERIFIED" / "HIGH CONFIDENCE" chips are noise.
// Other chips (RISING, HIGH IMPACT, ALERT) carry independent signal
// and stay regardless of confidence.
function isRedundantStatus(status: string, confidence: number | null): boolean {
  if (confidence === null || confidence < 0.85) return false;
  const s = status.toLowerCase();
  return (
    s.includes("confirmed") ||
    s.includes("verified") ||
    s.includes("high confidence")
  );
}

// Map free-form chip text to a severity bucket so the chip picks up
// the right colour. Mockup uses warm chips for "rising"/"high", neutral
// "confirmed"/"verified", green "saved"/"low".
function chipSeverity(status: string): "warn" | "good" | "info" | "neutral" {
  const s = status.toLowerCase();
  if (s.includes("rising") || s.includes("high") || s.includes("alert")) {
    return "warn";
  }
  if (s.includes("saved") || s.includes("low") || s.includes("good")) {
    return "good";
  }
  if (s.includes("confirmed") || s.includes("verified")) {
    return "info";
  }
  return "neutral";
}

// ─── Variant: competitor_battlecard (mockup state 3) ───────────────────
export function CompetitorBattlecardCard({
  card,
  onDismiss,
}: {
  card: Card;
  onDismiss: () => void;
}) {
  const p = card.payload as CompetitorBattlecardPayload;
  return (
    <article className="agent-desk-ai-card is-competitor_battlecard">
      <HeroHead card={card} onDismiss={onDismiss} />
      <h3 className="agent-desk-ai-title">{p.competitor_name}</h3>
      {p.comparison?.length > 0 && (
        <table className="agent-desk-ai-compare">
          <thead>
            <tr>
              <th>Feature</th>
              <th>{p.competitor_name}</th>
              <th>Our plan</th>
            </tr>
          </thead>
          <tbody>
            {p.comparison.map((row, i) => (
              <tr key={i}>
                <td>{row.feature}</td>
                <td className="agent-desk-ai-compare-theirs">{row.theirs}</td>
                <td className="agent-desk-ai-compare-ours">{row.ours}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {p.hooks?.length > 0 && <PointList items={p.hooks} />}
      <WhyLine why={p.why} label="Why this surfaces" />
    </article>
  );
}

// ─── Variant: offer_presentation (mockup state 4) ──────────────────────
// ─── Variant: troubleshooting ──────────────────────────────────────────
// Numbered diagnostic steps the agent walks the customer through before
// escalating. Renders symptom + likely_cause as a row pair (mirrors
// intent_root_cause), then the steps as a numbered list with the
// "expected" outcome as a small caption per step. escalate_if surfaces
// as a small footer chip so the agent knows when to bail to a ticket.
export function TroubleshootingCard({
  card,
  onDismiss,
}: {
  card: Card;
  onDismiss: () => void;
}) {
  const p = card.payload as TroubleshootingPayload;
  return (
    <article className="agent-desk-ai-card is-troubleshooting">
      <HeroHead card={card} onDismiss={onDismiss} />
      <h3 className="agent-desk-ai-title">{card.title}</h3>
      {(p.symptom || p.likely_cause) && (
        <div className="agent-desk-ai-rows">
          {p.symptom && (
            <div className="agent-desk-ai-row">
              <span className="agent-desk-ai-row-k">SYMPTOM</span>
              <span className="agent-desk-ai-row-v">{p.symptom}</span>
            </div>
          )}
          {p.likely_cause && (
            <div className="agent-desk-ai-row">
              <span className="agent-desk-ai-row-k">LIKELY CAUSE</span>
              <span className="agent-desk-ai-row-v">{p.likely_cause}</span>
            </div>
          )}
        </div>
      )}
      {p.steps?.length > 0 && (
        <ol className="agent-desk-ai-steps">
          {p.steps.map((s, i) => (
            <li key={i} className="agent-desk-ai-step">
              <span className="agent-desk-ai-step-text">{s.step}</span>
              {s.expected && (
                <span className="agent-desk-ai-step-expected">
                  → {s.expected}
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
      {p.escalate_if && (
        <div
          className="agent-desk-ai-troubleshooting-escalate"
          role="status"
          title="If this condition holds after the steps above, switch to a repair ticket."
        >
          <span className="agent-desk-ai-troubleshooting-escalate-label">
            Escalate if:
          </span>{" "}
          <span>{p.escalate_if}</span>
        </div>
      )}
    </article>
  );
}

// ─── Variant: repair_action ────────────────────────────────────────────
// One concrete next step + ETA, with a severity chip that drives the
// card's overall colour treatment. Built to be glanceable — agent reads
// it once and acts. Independent of troubleshooting; can fire when the
// symptom obviously needs a ticket without a diagnostic flow first.
export function RepairActionCard({
  card,
  onDismiss,
}: {
  card: Card;
  onDismiss: () => void;
}) {
  const p = card.payload as RepairActionPayload;
  const sev = p.severity ?? "medium";
  return (
    <article className={`agent-desk-ai-card is-repair_action is-sev-${sev}`}>
      <HeroHead card={card} onDismiss={onDismiss} />
      <h3 className="agent-desk-ai-title">{card.title}</h3>
      <div className="agent-desk-ai-rows">
        {p.issue_type && (
          <div className="agent-desk-ai-row">
            <span className="agent-desk-ai-row-k">ISSUE</span>
            <span className="agent-desk-ai-row-v">{p.issue_type}</span>
            <span className={`agent-desk-ai-row-status is-sev-${sev}`}>
              {sev.toUpperCase()}
            </span>
          </div>
        )}
        {p.ticket_action && (
          <div className="agent-desk-ai-row">
            <span className="agent-desk-ai-row-k">ACTION</span>
            <span className="agent-desk-ai-row-v">{p.ticket_action}</span>
          </div>
        )}
        {p.eta && (
          <div className="agent-desk-ai-row">
            <span className="agent-desk-ai-row-k">ETA</span>
            <span className="agent-desk-ai-row-v">{p.eta}</span>
          </div>
        )}
      </div>
      <WhyLine why={p.why} label="Why a ticket" />
    </article>
  );
}

export function OfferPresentationCard({
  card,
  callId,
  onDismiss,
}: {
  card: Card;
  /** Optional — when provided, an offer-ladder strip renders below the
   * card showing the alternative rungs from /api/calls/:id/eligible-offers.
   * AdviceBench mounts this component without a callId so the bench
   * stays self-contained (no fetches). */
  callId?: number | undefined;
  onDismiss: () => void;
}) {
  const p = card.payload as OfferPresentationPayload;
  const savings =
    p.current_monthly_cents > 0
      ? p.current_monthly_cents - p.new_monthly_cents
      : 0;
  const yearly = savings * 12;
  return (
    <article className="agent-desk-ai-card is-offer_presentation">
      <HeroHead card={card} onDismiss={onDismiss} />
      <h3 className="agent-desk-ai-title">{card.title}</h3>

      <div className="agent-desk-ai-impact">
        <div className="agent-desk-ai-impact-cell">
          <span className="agent-desk-ai-impact-k">Current</span>
          <span className="agent-desk-ai-impact-v was">
            {fmtCents(p.current_monthly_cents)}/mo
          </span>
        </div>
        <span className="agent-desk-ai-impact-arrow" aria-hidden>→</span>
        <div className="agent-desk-ai-impact-cell">
          <span className="agent-desk-ai-impact-k">With offer</span>
          <span className="agent-desk-ai-impact-v now">
            {fmtCents(p.new_monthly_cents)}/mo
          </span>
          {savings > 0 && (
            <span className="agent-desk-ai-impact-save">
              ↓ {fmtCents(savings)}/mo · {fmtCents(yearly)}/yr
            </span>
          )}
        </div>
      </div>

      {(p.lock_months > 0 || p.features?.length > 0) && (
        <div className="agent-desk-ai-grid">
          {p.lock_months > 0 && (
            <div className="agent-desk-ai-grid-row">
              <span className="agent-desk-ai-grid-k">Lock-in</span>
              <span className="agent-desk-ai-grid-v">
                {p.lock_months} months
              </span>
            </div>
          )}
          {p.features?.length > 0 && (
            <div className="agent-desk-ai-grid-row">
              <span className="agent-desk-ai-grid-k">Includes</span>
              <span className="agent-desk-ai-grid-v">
                {p.features.join(" · ")}
              </span>
            </div>
          )}
          <div className="agent-desk-ai-grid-row">
            <span className="agent-desk-ai-grid-k">Offer code</span>
            <span className="agent-desk-ai-grid-v mono">{p.offer_code}</span>
          </div>
        </div>
      )}

      {p.leads?.length > 0 && <PointList items={p.leads} />}
      <WhyLine why={p.why} label="Why this offer" />
      {callId !== undefined && (
        <OfferLadder
          callId={callId}
          currentMonthlyCents={p.current_monthly_cents}
          presentedOfferCode={p.offer_code}
        />
      )}
    </article>
  );
}

// ─── Variant: call_outcome (mockup state 5) ────────────────────────────
// End-of-call summary. Green-themed (saved) variant; non-saves keep the
// dashed AI border. Shows the outcome rows and the auto outcome tag.
export function CallOutcomeCard({
  card,
  onDismiss,
}: {
  card: Card;
  onDismiss: () => void;
}) {
  const p = card.payload as CallOutcomePayload;
  const outcomeMod = `is-outcome-${p.outcome.toLowerCase()}`;
  return (
    <article
      className={`agent-desk-ai-card is-call_outcome ${outcomeMod}`}
    >
      <header className="agent-desk-ai-card-head">
        <span className="agent-desk-ai-tag">
          <span className="agent-desk-ai-sparkle" aria-hidden>✓</span>
          {TYPE_TAG.call_outcome}
        </span>
        <span className="agent-desk-ai-confidence">{p.outcome}</span>
        <button
          type="button"
          className="agent-desk-ai-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
          title="Dismiss"
        >
          ×
        </button>
      </header>
      <h3 className="agent-desk-ai-title">{card.title}</h3>
      {p.rows?.length > 0 && (
        <div className="agent-desk-ai-outcome-rows">
          {p.rows.map((row, i) => (
            <div key={i} className="agent-desk-ai-outcome-row">
              <span className="agent-desk-ai-outcome-k">{row.label}</span>
              <span className="agent-desk-ai-outcome-v">{row.value}</span>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

// ─── Variant: coaching (mockup state 5) ────────────────────────────────
// Per-call coaching summary. Score grid + what went well + one thing
// to try next + weekly trend. Trend numbers are best-effort from the
// LLM; dashes are fine when no historical data is available.
export function CoachingCard({
  card,
  onDismiss,
}: {
  card: Card;
  onDismiss: () => void;
}) {
  const p = card.payload as CoachingPayload;
  return (
    <article className="agent-desk-ai-card is-coaching">
      <header className="agent-desk-ai-card-head">
        <span className="agent-desk-ai-tag">
          <span className="agent-desk-ai-sparkle" aria-hidden>✦</span>
          {TYPE_TAG.coaching}
        </span>
        <span className="agent-desk-ai-coaching-overall">
          Overall · {p.overall_score} / 100
        </span>
        <button
          type="button"
          className="agent-desk-ai-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
          title="Dismiss"
        >
          ×
        </button>
      </header>
      <h3 className="agent-desk-ai-title">{p.summary || card.title}</h3>

      {p.scores?.length > 0 && (
        <div className="agent-desk-ai-score-grid">
          {p.scores.map((s, i) => (
            <div key={i} className="agent-desk-ai-score">
              <div className="agent-desk-ai-score-head">
                <span className="agent-desk-ai-score-label">{s.label}</span>
                <span className="agent-desk-ai-score-num">{s.value}</span>
              </div>
              <div className="agent-desk-ai-score-bar">
                <div
                  className={`agent-desk-ai-score-fill is-${s.tier}`}
                  style={{ width: `${Math.max(0, Math.min(100, s.value))}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {p.what_went_well && (
        <CoachingMomentBlock
          label="What went well"
          variant="went-well"
          moment={p.what_went_well}
        />
      )}
      {p.one_thing_to_try_next && (
        <CoachingMomentBlock
          label="One thing to try next time"
          variant="next-time"
          moment={p.one_thing_to_try_next}
        />
      )}

      {p.trend && (
        <div className="agent-desk-ai-trend">
          <div className="agent-desk-ai-trend-head">Your trend</div>
          <div className="agent-desk-ai-trend-stats">
            <TrendStat value={p.trend.save_rate_this_week} label="Save rate · this week" />
            <span className="agent-desk-ai-trend-divider" aria-hidden />
            <TrendStat value={p.trend.save_rate_last_week} label="Last week" />
            <span className="agent-desk-ai-trend-divider" aria-hidden />
            <TrendStat value={p.trend.competitor_saves_today} label="Competitor saves today" />
          </div>
          {p.trend.summary && (
            <div className="agent-desk-ai-trend-summary">{p.trend.summary}</div>
          )}
        </div>
      )}
    </article>
  );
}

function CoachingMomentBlock({
  label,
  variant,
  moment,
}: {
  label: string;
  variant: "went-well" | "next-time";
  moment: CoachingMoment;
}) {
  return (
    <div className={`agent-desk-ai-moment is-${variant}`}>
      <div className="agent-desk-ai-moment-label">{label}</div>
      <div className="agent-desk-ai-moment-body">
        <span className="agent-desk-ai-moment-ts">
          {fmtMomentTs(moment.timestamp_secs)}
        </span>
        {moment.text}
      </div>
      {moment.quote && (
        <div className="agent-desk-ai-moment-quote">{moment.quote}</div>
      )}
    </div>
  );
}

function TrendStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="agent-desk-ai-trend-stat">
      <span className="agent-desk-ai-trend-num">{value || "—"}</span>
      <span className="agent-desk-ai-trend-lbl">{label}</span>
    </div>
  );
}

function fmtMomentTs(secs: number): string {
  if (!Number.isFinite(secs) || secs < 0) return "—";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

// ─── Slim nudge banner ─────────────────────────────────────────────────
export function NudgeBanner({
  card,
  severity,
  onDismiss,
}: {
  card: Card;
  severity: "alert" | "sentiment";
  onDismiss: () => void;
}) {
  const p = card.payload as NudgePayload | null;
  const message = p?.message ?? card.body;
  const evidence = p?.evidence ?? card.evidence;
  const icon = severity === "alert" ? "!" : "~";
  return (
    <div className={`agent-desk-advice-banner is-${severity}`} role="status">
      <span className="agent-desk-advice-banner-icon" aria-hidden>{icon}</span>
      <div className="agent-desk-advice-banner-body">
        <strong>{card.title}</strong>
        {message && (
          <span className="agent-desk-advice-banner-text"> · {message}</span>
        )}
        {evidence && (
          <em className="agent-desk-advice-banner-quote"> — "{evidence}"</em>
        )}
      </div>
      <button
        type="button"
        className="agent-desk-advice-banner-dismiss"
        onClick={onDismiss}
        aria-label="Dismiss"
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

// ─── Empty state ───────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div className="agent-desk-live-advice-empty">
      <div className="agent-desk-live-advice-empty-eyebrow">✦ CO-PILOT</div>
      <p>
        Listening to the call. Suggestions surface here as the conversation
        develops.
      </p>
    </div>
  );
}

// ─── Hero pagination ───────────────────────────────────────────────────
// Prev / next stepper rendered just below the hero card. Lets the agent
// step back through previous suggestions without leaving the canvas.
// "↻ Latest" snaps back to live tracking when pinned earlier.
function HeroPagination({
  currentIdx,
  total,
  onPrev,
  onNext,
  onLatest,
  isPinned,
}: {
  currentIdx: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onLatest: () => void;
  isPinned: boolean;
}) {
  const atFirst = currentIdx <= 0;
  const atLast = currentIdx >= total - 1;
  return (
    <div className="agent-desk-live-advice-pager" role="group" aria-label="Suggestion pager">
      <button
        type="button"
        className="agent-desk-live-advice-pager-btn"
        onClick={onPrev}
        disabled={atFirst}
        aria-label="Previous suggestion"
        title="Previous suggestion"
      >
        ← Previous
      </button>
      <span className="agent-desk-live-advice-pager-count">
        {currentIdx + 1} of {total}
      </span>
      <button
        type="button"
        className="agent-desk-live-advice-pager-btn"
        onClick={onNext}
        disabled={atLast}
        aria-label="Next suggestion"
        title="Next suggestion"
      >
        Next →
      </button>
      {isPinned && (
        <button
          type="button"
          className="agent-desk-live-advice-pager-latest"
          onClick={onLatest}
          title="Jump to the latest suggestion"
        >
          ↻ Latest
        </button>
      )}
    </div>
  );
}

// ─── Timeline navigator ────────────────────────────────────────────────
// Compact pill strip of every hero card emitted during the call. Slim
// indicators only — type-color dot + time, no title preview. The hero
// itself is the canvas content; the timeline is just navigation.
function Navigator({
  cards,
  activeId,
  callStatus,
  onPick,
}: {
  cards: Card[];
  activeId: number | null;
  callStatus: CallStatus | undefined;
  onPick: (id: number) => void;
}) {
  return (
    <nav
      id="agent-desk-live-advice-nav"
      className={`agent-desk-live-advice-nav is-${callStatus ?? "active"}`}
      aria-label="Suggestion timeline"
    >
      <div className="agent-desk-live-advice-nav-head">
        <span className="agent-desk-live-advice-nav-label">
          {callStatus === "wrapup" ? "Call timeline" : "Earlier suggestions"}
        </span>
      </div>
      <ol className="agent-desk-live-advice-nav-list">
        {cards.map((card, i) => (
          <li key={card.id}>
            <button
              type="button"
              className={`agent-desk-live-advice-nav-pill is-${card.type} ${
                card.id === activeId ? "is-active" : ""
              }`}
              onClick={() => onPick(card.id)}
              title={`${TYPE_TAG[card.type] ?? card.type} · ${fmtTime(card.ts)} — ${card.title}`}
              aria-label={`Suggestion ${i + 1}: ${card.title}`}
            >
              <span className="agent-desk-live-advice-nav-pill-dot" aria-hidden />
              <span className="agent-desk-live-advice-nav-pill-time">
                {fmtTime(card.ts)}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
}
